import { applyCommands } from './commands';
import { withTickDerived } from './derived';
import { automation } from './systems/automation';
import { combat } from './systems/combat';
import { construction } from './systems/construction';
import { economy } from './systems/economy';
import { exploration } from './systems/exploration';
import { formation } from './systems/formation';
import { founding } from './systems/founding';
import { guard } from './systems/guard';
import { heal, repair } from './systems/heal';
import { movement, recordInFlight } from './systems/movement';
import { pursuit } from './systems/pursuit';
import { spies } from './systems/spies';
import { storage } from './systems/storage';
import { tend } from './systems/tend';
import { training } from './systems/training';
import { upgrades } from './systems/upgrades';
import { upkeep } from './systems/upkeep';
import { victory } from './systems/victory';
import type { PlayerInput, SimContext, SimState, SystemFn } from './types';

/**
 * Pipeline contract (order is significant): commands before movement so
 * same-tick move commands advance this tick; construction before economy so
 * a farm completed this tick is gatherable the same tick; combat before
 * victory so kills are reflected in elimination checks immediately; heal
 * right after combat, so only this tick's survivors are mended, and repair
 * (buildings undamaged for a while) after it; spies then
 * drops disguises that no longer hold (moved, left, city fallen) and
 * catches undisguised spies that overstay in rival land.
 * Training runs right after commands, so a unit done training (or queued
 * this tick) starts out like one spawned by a command; its progress counts
 * the tick it was queued in.
 * Automation runs after commands (a manual order this tick wins) and before
 * movement, and steps pinned workers' job loops too (`assign`); storage deploys threatened barracks before anything plans a
 * path; guard then sends idle defenders at their city's attackers;
 * pursuit steers attack orders, then formation keeps groups together and
 * tend sends healers to the wounded (over formation), just before
 * movement (which also settles units onto free tiles);
 * founding then turns settlers that stopped on their `foundAt` tile into
 * city sites, before construction; upgrades advance (and finish) timed building
 * upgrades right after it, before the economy;
 * upkeep is paid after the economy's deposits and before combat;
 * exploration records vision after everything has moved. recordInFlight
 * notes who is moving at tick start, for settle.
 *
 * The systems share one `TickDerived` layer (derived.ts): what reads paths is
 * dropped after every system, the rest when units move, deploy or die or
 * buildings change (`invalidateDerived`), and once the commands are in.
 */
const SIM_PIPELINE: readonly SystemFn[] = [
  recordInFlight,
  applyCommands,
  training,
  automation,
  storage,
  guard,
  pursuit,
  formation,
  tend,
  movement,
  founding,
  construction,
  upgrades,
  economy,
  upkeep,
  combat,
  heal,
  repair,
  spies,
  exploration,
  victory,
];

/**
 * Runs every system of SIM_PIPELINE once, sharing one `TickDerived` layer
 * (see above). `around` (the host bench) wraps each system's run, e.g. to
 * time it; it must call `run` exactly once.
 */
function runPipeline(
  state: SimState,
  ctx: SimContext,
  inputs: readonly PlayerInput[],
  dt: number,
  around?: (index: number, run: () => void) => void
): void {
  withTickDerived(state, ctx, (derived) => {
    SIM_PIPELINE.forEach((system, index) => {
      const run = () => system(state, ctx, inputs, dt);
      if (around) around(index, run);
      else run();
      derived.endSystem();
      // Commands place, spawn, deploy and upgrade: start from a clean layer.
      if (system === applyCommands) derived.invalidate();
    });
  });
}

/**
 * Advances the simulation by one step of duration dt seconds.
 * No-ops if state.status !== 'running'.
 * Counters (tick, elapsed) are incremented AFTER all systems run.
 */
function tick(state: SimState, ctx: SimContext, inputs: readonly PlayerInput[], dt: number): void {
  if (state.status !== 'running') return;

  runPipeline(state, ctx, inputs, dt);

  state.tick += 1;
  state.elapsed += dt;
}

export { runPipeline, SIM_PIPELINE, tick };
